import type { TimelineStop } from '../../dispatch/features/trip-timeline/trip-timeline.api';
import { durationText } from '../../../shared/policies/duration-text';
import type { TripEta } from '../trips.api';

export interface TripStop {
  position: number;
  name: string;
  lat: number | null;
  lng: number | null;
  plannedAt: string | null;
  /** Late is positive. From the arrival when the truck has arrived, else from the latest estimate; null when neither can be told. */
  varianceSeconds: number | null;
  hasArrived: boolean;
}

export interface TripStopRow {
  label: string;
  text: string;
  tone: 'ok' | 'risk' | 'neutral';
}

const timeFormat = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

/** The plan's stops with how far each is from its planned arrival: the arrival when it happened, otherwise the newest estimate. */
export function tripStops(timeline: TimelineStop[], eta: TripEta | null): TripStop[] {
  const estimates = new Map((eta?.recalculation?.stops ?? []).map((stop) => [stop.position, stop]));
  return timeline.map((stop) => {
    const hasArrived = stop.actuals.some((actual) => actual.milestone === 'arrived');
    const estimated = estimates.get(stop.position);
    const projected = estimated && !estimated.isReached ? (estimated.varianceSeconds ?? null) : null;
    return {
      position: stop.position,
      name: stop.name,
      lat: stop.lat,
      lng: stop.lng,
      plannedAt: stop.planned.arrivalAt,
      varianceSeconds: hasArrived ? stop.variance.arrivalSeconds : projected,
      hasArrived,
    };
  });
}

function varianceText(stop: TripStop): Pick<TripStopRow, 'text' | 'tone'> {
  const { varianceSeconds, hasArrived } = stop;
  if (varianceSeconds === null) return { text: hasArrived ? 'arrived' : 'no estimate yet', tone: 'neutral' };
  const spell = hasArrived ? 'arrived' : 'expected';
  if (Math.abs(varianceSeconds) < 60) return { text: `${spell} on time`, tone: 'ok' };
  return varianceSeconds > 0
    ? { text: `${spell} ${durationText(varianceSeconds)} late`, tone: 'risk' }
    : { text: `${spell} ${durationText(varianceSeconds)} early`, tone: 'ok' };
}

/** One header line per stop that has a planned time: "Mombasa port, planned 15:00, expected 40 min late". */
export function stopRows(stops: TripStop[]): TripStopRow[] {
  return stops
    .filter((stop) => stop.plannedAt !== null)
    .map((stop) => {
      const variance = varianceText(stop);
      return { label: stop.name, tone: variance.tone, text: `planned ${timeFormat.format(new Date(stop.plannedAt as string))}, ${variance.text}` };
    });
}
